"use client"

import * as React from "react"
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "../../components/resizable"
import { cn } from "../../lib/utils"
import { cva, type VariantProps } from "class-variance-authority"
import { UITypography } from "../display/ui-typography"
import { UIBox } from "./ui-box"

// ============================================================================
// UIResizable Types
// ============================================================================

export interface UIResizableProps extends VariantProps<typeof resizableVariants> {
  /**
   * 패널 방향
   */
  direction?: 'horizontal' | 'vertical'
  /**
   * 초기 패널 크기들 (백분율)
   */
  defaultSizes?: number[]
  /**
   * 최소 패널 크기들 (백분율)
   */
  minSizes?: number[]
  /**
   * 최대 패널 크기들 (백분율)
   */
  maxSizes?: number[]
  /**
   * 리사이즈 핸들 스타일
   */
  handleStyle?: 'default' | 'minimal' | 'with-icon' | 'custom'
  /**
   * 패널 내용들
   */
  children: React.ReactNode[]
  /**
   * 리사이즈 이벤트 콜백
   */
  onResize?: (sizes: number[]) => void
  /**
   * 컨테이너 클래스명
   */
  className?: string
  /**
   * 패널 간 간격
   */
  gap?: number
  /**
   * 핸들 표시 여부
   */
  showHandle?: boolean
  /**
   * 접을 수 있는 패널 여부
   */
  collapsible?: boolean
  /**
   * 접힌 패널 크기 (백분율)
   */
  collapsedSize?: number
}

const resizableVariants = cva(
  "w-full max-w-full min-w-0 flex-shrink-0 h-full", // Width Stability 패턴
  {
    variants: {
      variant: {
        default: "bg-background",
        bordered: "border border-border rounded-lg overflow-hidden",
        elevated: "border border-border rounded-xl shadow-lg overflow-hidden",
        minimal: "bg-transparent",
      },
      spacing: {
        none: "gap-0",
        sm: "gap-1",
        default: "gap-2",
        lg: "gap-4",
      },
    },
    defaultVariants: {
      variant: "default",
      spacing: "default",
    },
  }
)

// ============================================================================
// UIResizable Component
// ============================================================================

export const UIResizable = React.forwardRef<
  React.ComponentRef<typeof ResizablePanelGroup>,
  UIResizableProps
>(({
  direction = 'horizontal',
  defaultSizes,
  minSizes,
  maxSizes,
  handleStyle = 'default',
  children,
  onResize,
  className,
  variant,
  spacing,
  gap,
  showHandle = true,
  collapsible = false,
  collapsedSize = 5,
  ...props
}, ref) => {
  const [sizes, setSizes] = React.useState<number[]>(defaultSizes || [])

  const handleLayout = (newSizes: number[]) => {
    setSizes(newSizes)
    onResize?.(newSizes)
  }

  // 자동으로 균등 분할 계산
  const panelCount = React.Children.count(children)
  const equalSizes = React.useMemo(() => {
    if (defaultSizes && defaultSizes.length === panelCount) {
      return defaultSizes
    }
    return Array(panelCount).fill(100 / panelCount)
  }, [panelCount, defaultSizes])

  return (
    <ResizablePanelGroup
      ref={ref}
      direction={direction}
      onLayout={handleLayout}
      className={cn(resizableVariants({ variant, spacing }), className)}
      // Wire gap prop: inline style overrides the spacing variant gap when explicitly provided
      style={gap !== undefined ? { gap: `${gap * 0.25}rem` } : undefined}
      {...props}
    >
      {React.Children.map(children, (child, index) => (
        <React.Fragment key={index}>
          <ResizablePanel
            defaultSize={equalSizes[index]}
            minSize={minSizes?.[index]}
            maxSize={maxSizes?.[index]}
            collapsible={collapsible}
            collapsedSize={collapsedSize}
            className="relative"
          >
            <UIBox className="h-full w-full overflow-auto">
              {child}
            </UIBox>
          </ResizablePanel>

          {/* 마지막 패널이 아닌 경우에만 핸들 추가 */}
          {index < panelCount - 1 && showHandle && (
            <ResizableHandle
              withHandle={handleStyle === 'with-icon'}
              className={cn(
                // 핸들 스타일링
                handleStyle === 'minimal' && "w-[1px] bg-border/50 hover:bg-border",
                handleStyle === 'default' && "w-[1px] bg-border hover:bg-accent",
                handleStyle === 'with-icon' && "w-2 bg-muted hover:bg-accent",
                handleStyle === 'custom' && "w-1 bg-gradient-to-b from-border/0 via-border to-border/0",

                // 방향별 스타일링
                direction === 'vertical' && [
                  handleStyle === 'minimal' && "h-[1px] w-full",
                  handleStyle === 'default' && "h-[1px] w-full",
                  handleStyle === 'with-icon' && "h-2 w-full",
                  handleStyle === 'custom' && "h-1 w-full bg-gradient-to-r from-border/0 via-border to-border/0",
                ],

                // 전환 효과
                "transition-colors duration-200"
              )}
            />
          )}
        </React.Fragment>
      ))}
    </ResizablePanelGroup>
  )
})

UIResizable.displayName = "UIResizable"

// ============================================================================
// Preset Components
// ============================================================================

export interface UIResizableSidebarProps extends Omit<UIResizableProps, 'children' | 'direction'> {
  /**
   * 사이드바 내용
   */
  sidebar: React.ReactNode
  /**
   * 메인 콘텐츠
   */
  children: React.ReactNode
  /**
   * 사이드바 위치
   */
  sidebarPosition?: 'left' | 'right'
  /**
   * 사이드바 기본 크기 (백분율)
   */
  sidebarSize?: number
  /**
   * 사이드바 최소 크기 (백분율)
   */
  sidebarMinSize?: number
  /**
   * 사이드바 최대 크기 (백분율)
   */
  sidebarMaxSize?: number
}

/**
 * 사이드바 + 메인 콘텐츠 레이아웃
 */
export const UIResizableSidebar = React.forwardRef<
  React.ComponentRef<typeof ResizablePanelGroup>,
  UIResizableSidebarProps
>(({
  sidebar,
  children,
  sidebarPosition = 'left',
  sidebarSize = 25,
  sidebarMinSize = 15,
  sidebarMaxSize = 50,
  variant = 'bordered',
  ...props
}, ref) => {
  const sidebarContent = (
    <UIBox className="h-full p-4 bg-muted/50 border-r border-border">
      {sidebar}
    </UIBox>
  )

  const mainContent = (
    <UIBox className="h-full p-4">
      {children}
    </UIBox>
  )

  return (
    <UIResizable
      ref={ref}
      direction="horizontal"
      defaultSizes={
        sidebarPosition === 'left'
          ? [sidebarSize, 100 - sidebarSize]
          : [100 - sidebarSize, sidebarSize]
      }
      minSizes={
        sidebarPosition === 'left'
          ? [sidebarMinSize, 100 - sidebarMaxSize]
          : [100 - sidebarMaxSize, sidebarMinSize]
      }
      maxSizes={
        sidebarPosition === 'left'
          ? [sidebarMaxSize, 100 - sidebarMinSize]
          : [100 - sidebarMinSize, sidebarMaxSize]
      }
      variant={variant}
      collapsible={true}
      collapsedSize={5}
      {...props}
    >
      {sidebarPosition === 'left' ? [sidebarContent, mainContent] : [mainContent, sidebarContent]}
    </UIResizable>
  )
})

UIResizableSidebar.displayName = "UIResizableSidebar"

export interface UIResizablePanelsProps extends Omit<UIResizableProps, 'children'> {
  /**
   * 패널 설정들
   */
  panels: Array<{
    id?: string
    content: React.ReactNode
    defaultSize?: number
    minSize?: number
    maxSize?: number
    collapsible?: boolean
    title?: string
    className?: string
  }>
}

/**
 * N개 패널 자유 레이아웃
 */
export const UIResizablePanels = React.forwardRef<
  React.ComponentRef<typeof ResizablePanelGroup>,
  UIResizablePanelsProps
>(({
  panels,
  variant = 'bordered',
  ...props
}, ref) => {
  const defaultSizes = panels.map(panel => panel.defaultSize || (100 / panels.length))
  const minSizes = panels.map(panel => panel.minSize || 10)
  const maxSizes = panels.map(panel => panel.maxSize || 80)

  return (
    <UIResizable
      ref={ref}
      defaultSizes={defaultSizes}
      minSizes={minSizes}
      maxSizes={maxSizes}
      variant={variant}
      collapsible={true}
      {...props}
    >
      {panels.map((panel, index) => (
        <UIBox key={panel.id || index} className={cn("h-full flex flex-col", panel.className)}>
          {panel.title && (
            <UIBox className="flex-shrink-0 px-4 py-2 border-b border-border bg-muted/30">
              <UITypography variant="h3" size="sm" weight="medium">{panel.title}</UITypography>
            </UIBox>
          )}
          <UIBox className="flex-1 p-4 overflow-auto">
            {panel.content}
          </UIBox>
        </UIBox>
      ))}
    </UIResizable>
  )
})

UIResizablePanels.displayName = "UIResizablePanels"

export interface UIResizableEditorProps extends Omit<UIResizableProps, 'children' | 'direction'> {
  /**
   * 에디터 내용
   */
  editor: React.ReactNode
  /**
   * 프리뷰 내용
   */
  preview: React.ReactNode
  /**
   * 레이아웃 방향
   */
  layout?: 'horizontal' | 'vertical'
  /**
   * 에디터 기본 크기 (백분율)
   */
  editorSize?: number
  /**
   * 실시간 프리뷰 여부
   */
  livePreview?: boolean
  /**
   * 도구모음 표시 여부
   */
  showToolbar?: boolean
  /**
   * 도구모음 내용
   */
  toolbar?: React.ReactNode
}

/**
 * 에디터 스타일 (코드 + 프리뷰)
 */
export const UIResizableEditor = React.forwardRef<
  React.ComponentRef<typeof ResizablePanelGroup>,
  UIResizableEditorProps
>(({
  editor,
  preview,
  layout = 'horizontal',
  editorSize = 50,
  livePreview = true,
  showToolbar = false,
  toolbar,
  variant = 'bordered',
  ...props
}, ref) => {
  const editorPanel = (
    <UIBox className="h-full flex flex-col bg-background">
      {showToolbar && toolbar && (
        <UIBox className="flex-shrink-0 px-4 py-2 border-b border-border bg-muted/30">
          {toolbar}
        </UIBox>
      )}
      <UIBox className="flex-1 relative">
        <UIBox className="absolute inset-0 overflow-auto">
          {editor}
        </UIBox>
      </UIBox>
    </UIBox>
  )

  const previewPanel = (
    <UIBox className="h-full flex flex-col bg-background">
      <UIBox className="flex-shrink-0 px-4 py-2 border-b border-border bg-muted/30 flex items-center justify-between">
        <UITypography variant="h3" size="sm" weight="medium">Preview</UITypography>
        {livePreview && (
          <UIBox className="flex items-center gap-2 text-xs text-muted-foreground">
            <UIBox className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            Live
          </UIBox>
        )}
      </UIBox>
      <UIBox className="flex-1 p-4 overflow-auto bg-white dark:bg-slate-950">
        {preview}
      </UIBox>
    </UIBox>
  )

  return (
    <UIResizable
      ref={ref}
      direction={layout}
      defaultSizes={[editorSize, 100 - editorSize]}
      minSizes={[20, 20]}
      maxSizes={[80, 80]}
      variant={variant}
      handleStyle="with-icon"
      {...props}
    >
      {[editorPanel, previewPanel]}
    </UIResizable>
  )
})

UIResizableEditor.displayName = "UIResizableEditor"

// ============================================================================
// Individual Component Exports
// ============================================================================

export const UIResizablePanel = ResizablePanel
export const UIResizableHandle = ResizableHandle
export const UIResizablePanelGroup = ResizablePanelGroup

// ============================================================================
// Type Re-exports
// ============================================================================
// Note: All types are automatically exported via their interface declarations